import React, { useState, useEffect } from 'react';
import TotpChallengeWidget from './TotpChallengeWidget';
import BackupCodeChallengeWidget from './BackupCodeChallengeWidget';
import { challengeFactor, verifyFactor } from '../../utils/mfaLoginApi';
import { useAuth } from '../Auth/AuthContext';

/**
 * MfaChallenge
 *
 * Props:
 *   mfaToken: string                  — held in parent (Login.js) state, never persisted
 *   availableFactors: string[]        — e.g., ["totp", "backup_codes"]
 *   onCancel(): void                  — return to email/password form
 *   onError(message): void            — propagate fatal errors (expired token, etc.)
 *
 * Internally:
 *   - selectedType: "totp" | "backup_codes"
 *   - calls challengeFactor on mount and on type switch (no-op for TOTP, sends OTP for SMS)
 *   - on successful verify, calls auth.completeMfaLogin(verifyResponseBody)
 */
export default function MfaChallenge({ mfaToken, availableFactors, userRole, onCancel, onError }) {
  const auth = useAuth();
  const isAdmin = userRole === 'admin';
  const defaultType = availableFactors.includes('totp') ? 'totp' : availableFactors[0];
  const [selectedType, setSelectedType] = useState(defaultType);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);

  // Initiate challenge on mount + on type switch
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await challengeFactor(mfaToken, selectedType);
      } catch (err) {
        if (cancelled) return;
        if (err.status === 401) {
          onError('Session timed out, please sign in again.');
          return;
        }
        setError(err.detail || err.message || 'Could not start verification');
      }
    })();
    return () => { cancelled = true; };
  }, [mfaToken, selectedType, onError]);

  const handleSubmit = async (code) => {
    setError('');
    setSubmitting(true);
    try {
      const data = await verifyFactor(mfaToken, selectedType, code, rememberDevice);
      // data has the same shape as /api/auth/login: { expires_in, user }
      auth.completeMfaLogin(data);
    } catch (err) {
      if (err.status === 401) {
        // Either bad code or expired mfa_token
        if (err.detail === 'invalid_code') {
          setError('That code is not valid. Try again.');
        } else {
          onError('Session timed out, please sign in again.');
        }
      } else if (err.status === 429) {
        setError('Too many attempts. Please wait and try again.');
      } else {
        setError(err.detail || err.message || 'Verification failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const backupAvailable = availableFactors.includes('backup_codes');

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-center text-2xl font-bold text-gray-50 drop-shadow-sm">
          Two-factor verification
        </h2>
        <p className="mt-2 text-center text-sm text-white">
          We need one more step to confirm it's you.
        </p>
      </div>

      {selectedType === 'totp' && (
        <TotpChallengeWidget
          onSubmit={handleSubmit}
          onSwitchToBackup={() => { setError(''); setSelectedType('backup_codes'); }}
          submitting={submitting}
          error={error}
          backupAvailable={backupAvailable}
          rememberDevice={rememberDevice}
          onRememberDeviceChange={setRememberDevice}
          showRememberDevice={!isAdmin}
        />
      )}

      {selectedType === 'backup_codes' && (
        <BackupCodeChallengeWidget
          onSubmit={handleSubmit}
          onSwitchToTotp={() => { setError(''); setSelectedType('totp'); }}
          submitting={submitting}
          error={error}
          rememberDevice={rememberDevice}
          onRememberDeviceChange={setRememberDevice}
          showRememberDevice={!isAdmin}
        />
      )}

      <button
        type="button"
        onClick={onCancel}
        className="block w-full text-center text-xs text-gray-300 hover:text-white"
      >
        Cancel and sign in as a different user
      </button>
    </div>
  );
}
